import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import {
  getSessionTranscript,
  listSessions,
  type TranscriptMessage,
} from "../../../../schedules/api";

export const Route = createFileRoute("/_workspace/settings/schedules/executed")(
  {
    component: ExecutedPage,
  },
);

function ExecutedPage() {
  const sessions = useQuery({
    queryKey: ["schedule-sessions"],
    queryFn: listSessions,
  });
  const [openChat, setOpenChat] = useState<string | null>(null);

  return (
    <div className="mx-auto w-full max-w-3xl px-6 pt-8 pb-12">
      <h1 className="mb-1 text-lg font-semibold">Executed</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        History of runs that have already fired.
      </p>
      {sessions.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : sessions.error ? (
        <p className="text-sm text-destructive">
          {(sessions.error as Error).message}
        </p>
      ) : sessions.data && sessions.data.length > 0 ? (
        <ul className="divide-y divide-border rounded-md border border-border">
          {sessions.data.map((run, i) => (
            <li
              key={`${run.scheduleId}-${run.firedAt}-${i}`}
              className="px-4 py-3"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {run.kind}: {run.ref}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(run.firedAt * 1000).toLocaleString()} ·{" "}
                    <span
                      className={
                        run.status === "error"
                          ? "text-destructive"
                          : "text-muted-foreground"
                      }
                    >
                      {run.status}
                    </span>
                  </div>
                </div>
                {run.chatId ? (
                  <button
                    type="button"
                    onClick={() =>
                      setOpenChat(openChat === run.chatId ? null : run.chatId)
                    }
                    className="shrink-0 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    {openChat === run.chatId ? "Hide" : "View"}
                  </button>
                ) : null}
              </div>
              {run.chatId && openChat === run.chatId ? (
                <Transcript chatId={run.chatId} />
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nothing has run yet.</p>
      )}
    </div>
  );
}

/** How often an in-progress run's transcript is re-read. */
const RUNNING_POLL_MS = 3000;

function Transcript({ chatId }: { chatId: string }) {
  const transcript = useQuery({
    queryKey: ["schedule-transcript", chatId],
    queryFn: () => getSessionTranscript(chatId),
    // A fire returns at intake now, so an open run's transcript is still being
    // written: poll until the thread says it settled. `running` comes from the
    // thread's own ledger, so the poll stops on a turn that failed without
    // writing an answer as well as on one that finished.
    refetchInterval: (query) =>
      query.state.data?.running ? RUNNING_POLL_MS : false,
  });
  if (transcript.isLoading) {
    return <p className="mt-2 text-xs text-muted-foreground">Loading…</p>;
  }
  if (transcript.error) {
    return (
      <p className="mt-2 text-xs text-destructive">
        {(transcript.error as Error).message}
      </p>
    );
  }
  const { messages = [], running = false } = transcript.data ?? {};
  if (messages.length === 0) {
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        {running ? "Running…" : "(empty)"}
      </p>
    );
  }
  return (
    <div className="mt-3 space-y-2 rounded-md bg-muted/40 p-3">
      {messages.map((m: TranscriptMessage) => (
        <div key={m.id} className="text-sm">
          <span className="mr-2 text-xs font-medium uppercase text-muted-foreground">
            {m.role}
          </span>
          <span className="whitespace-pre-wrap">{m.text}</span>
        </div>
      ))}
      {running ? (
        <p className="text-xs text-muted-foreground">Running…</p>
      ) : null}
    </div>
  );
}
